import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';

function Hero3D() {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Setup
    const container = containerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;

    // Scene
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // Camera
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    camera.position.z = 10;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ 
      antialias: true, 
      alpha: true,
      powerPreference: 'high-performance'
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Lights
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
    scene.add(ambientLight);

    const cyanLight = new THREE.PointLight(0x00E5FF, 1.5, 100);
    cyanLight.position.set(10, 10, 10);
    scene.add(cyanLight);

    const purpleLight = new THREE.PointLight(0xB026FF, 0.8, 100);
    purpleLight.position.set(-10, -10, -10);
    scene.add(purpleLight);

    const blueLight = new THREE.PointLight(0x2962FF, 0.5, 100);
    blueLight.position.set(0, -5, 5);
    scene.add(blueLight);

    // Core icosahedron
    const coreGeometry = new THREE.IcosahedronGeometry(2, 1);
    const coreMaterial = new THREE.MeshStandardMaterial({
      color: 0x00E5FF,
      emissive: 0x00E5FF,
      emissiveIntensity: 0.8,
      metalness: 0.9,
      roughness: 0.1,
      transparent: true,
      opacity: 0.9,
    });
    const core = new THREE.Mesh(coreGeometry, coreMaterial);
    scene.add(core);

    // Wireframe shell
    const wireGeometry = new THREE.IcosahedronGeometry(2.2, 1);
    const wireMaterial = new THREE.MeshBasicMaterial({
      color: 0xB026FF,
      wireframe: true,
      transparent: true,
      opacity: 0.5,
    });
    const wireframe = new THREE.Mesh(wireGeometry, wireMaterial);
    scene.add(wireframe);

    // Orbit rings
    const ringColors = [0x00E5FF, 0x2962FF, 0xB026FF];
    const rings = [];
    [3, 3.8, 4.6].forEach((radius, i) => {
      const ringGeometry = new THREE.TorusGeometry(radius, 0.02, 16, 100);
      const ringMaterial = new THREE.MeshBasicMaterial({
        color: ringColors[i],
        transparent: true,
        opacity: 0.4 + i * 0.1,
      });
      const ring = new THREE.Mesh(ringGeometry, ringMaterial);
      ring.rotation.x = Math.PI / 2;
      ring.rotation.y = (i * Math.PI) / 3;
      scene.add(ring);
      rings.push(ring);
    });

    // Ring group for animation
    const ringGroup = new THREE.Group();
    rings.forEach(ring => {
      scene.remove(ring);
      ringGroup.add(ring);
    });
    scene.add(ringGroup);

    // Floating shapes
    const shapeTypes = ['tetra', 'octa', 'dodeca'];
    const shapes = [];
    for (let i = 0; i < 10; i++) {
      let geometry;
      const shapeType = shapeTypes[i % 3];
      if (shapeType === 'tetra') {
        geometry = new THREE.TetrahedronGeometry(1, 0);
      } else if (shapeType === 'octa') {
        geometry = new THREE.OctahedronGeometry(1, 0);
      } else {
        geometry = new THREE.DodecahedronGeometry(1, 0);
      }
      
      const material = new THREE.MeshBasicMaterial({
        color: ringColors[i % 3],
        wireframe: true,
        transparent: true,
        opacity: 0.35,
      });
      
      const shape = new THREE.Mesh(geometry, material);
      const scale = Math.random() * 0.25 + 0.15;
      shape.scale.set(scale, scale, scale);
      shape.position.set(
        (Math.random() - 0.5) * 14,
        (Math.random() - 0.5) * 10,
        (Math.random() - 0.5) * 8 - 3
      );
      shape.userData = {
        baseY: shape.position.y,
        speed: Math.random() * 0.4 + 0.3,
        index: i
      };
      scene.add(shape);
      shapes.push(shape);
    }

    // Particles
    const particles = [];
    for (let i = 0; i < 50; i++) {
      const particleGeometry = new THREE.SphereGeometry(Math.random() * 0.05 + 0.02, 6, 6);
      const particleMaterial = new THREE.MeshBasicMaterial({
        color: 0x00E5FF,
        transparent: true,
        opacity: 0.6,
      });
      const particle = new THREE.Mesh(particleGeometry, particleMaterial);
      particle.position.set(
        (Math.random() - 0.5) * 20,
        (Math.random() - 0.5) * 20,
        (Math.random() - 0.5) * 20
      );
      scene.add(particle);
      particles.push(particle);
    }

    const particleGroup = new THREE.Group();
    particles.forEach(p => {
      scene.remove(p);
      particleGroup.add(p);
    });
    scene.add(particleGroup);

    // Mouse tracking
    const mouse = { x: 0, y: 0 };
    const handleMouseMove = (e) => {
      mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
      mouse.y = -(e.clientY / window.innerHeight) * 2 + 1;
    };
    window.addEventListener('mousemove', handleMouseMove);

    // Resize handler
    const handleResize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', handleResize);

    // Animation loop
    const clock = new THREE.Clock();
    
    function animate() {
      animationRef.current = requestAnimationFrame(animate);
      
      const t = clock.getElapsedTime();
      
      // Core animation
      core.rotation.x = t * 0.1;
      core.rotation.y = t * 0.15;
      const coreScale = 1 + Math.sin(t * 0.5) * 0.05;
      core.scale.setScalar(coreScale * 2);
      core.position.y = Math.sin(t * 0.5) * 0.3;
      
      // Wireframe animation
      wireframe.rotation.x = -t * 0.08;
      wireframe.rotation.y = -t * 0.12;
      const wireScale = 1.1 + Math.sin(t * 0.5 + 1) * 0.05;
      wireframe.scale.setScalar(wireScale * 2);
      wireframe.position.y = Math.sin(t * 0.5) * 0.3;
      
      // Ring group animation
      ringGroup.rotation.z = t * 0.05;
      ringGroup.rotation.x = Math.sin(t * 0.1) * 0.2;
      
      // Shapes animation
      shapes.forEach(shape => {
        const { baseY, speed, index } = shape.userData;
        shape.rotation.x = t * speed * 0.5;
        shape.rotation.y = t * speed * 0.3;
        shape.position.y = baseY + Math.sin(t * speed + index) * 0.5;
      });
      
      // Particles animation
      particleGroup.rotation.y = t * 0.02;
      particleGroup.rotation.x = t * 0.01;
      
      // Camera follows mouse
      camera.position.x += (mouse.x * 1.5 - camera.position.x) * 0.03;
      camera.position.y += (mouse.y * 0.8 - camera.position.y) * 0.03;
      camera.lookAt(0, 0, 0);
      
      renderer.render(scene, camera);
    }
    
    animate();

    // Cleanup
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationRef.current);
      
      if (rendererRef.current && container.contains(rendererRef.current.domElement)) {
        container.removeChild(rendererRef.current.domElement);
      }
      
      renderer.dispose();
    };
  }, []);

  return (
    <div 
      ref={containerRef} 
      className="absolute inset-0 w-full h-full"
      data-testid="hero-3d-canvas"
    />
  );
}

export default Hero3D;
